<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { settings, PATHS, zonesBySector } from '@/content'
import { isProPath } from '@/lib/section'

const route = useRoute()
const isPro = computed(() => isProPath(route.path))
const zoneSectors = zonesBySector()

const year = new Date().getFullYear()
const socials = [
  { name: 'Facebook', url: settings.socials.facebook, icon: 'M13 22v-8h2.7l.4-3H13V9c0-.9.2-1.5 1.5-1.5H16V4.9c-.3 0-1.2-.1-2.3-.1-2.3 0-3.7 1.4-3.7 3.9V11H7.5v3H10v8h3z' },
  { name: 'Instagram', url: settings.socials.instagram, icon: 'M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 1.8.3 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 1.2-.3 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2-.1-1.8-.3-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9c.1-1.2.3-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2m0 1.9c-3.1 0-3.5 0-4.7.1-1.1.1-1.7.2-2.1.4-.5.2-.9.4-1.3.8-.4.4-.6.8-.8 1.3-.2.4-.3 1-.4 2.1C2.6 9.9 2.6 10.3 2.6 12s0 2.1.1 3.3c.1 1.1.2 1.7.4 2.1.2.5.4.9.8 1.3.4.4.8.6 1.3.8.4.2 1 .3 2.1.4 1.2.1 1.6.1 4.7.1s3.5 0 4.7-.1c1.1-.1 1.7-.2 2.1-.4.5-.2.9-.4 1.3-.8.4-.4.6-.8.8-1.3.2-.4.3-1 .4-2.1.1-1.2.1-1.6.1-3.3s0-2.1-.1-3.3c-.1-1.1-.2-1.7-.4-2.1-.2-.5-.4-.9-.8-1.3-.4-.4-.8-.6-1.3-.8-.4-.2-1-.3-2.1-.4-1.2-.1-1.6-.1-4.7-.1m0 3.3a4.6 4.6 0 100 9.2 4.6 4.6 0 000-9.2m0 7.6a3 3 0 110-6 3 3 0 010 6m5.9-7.8a1.1 1.1 0 11-2.2 0 1.1 1.1 0 012.2 0' },
  { name: 'LinkedIn', url: settings.socials.linkedin, icon: 'M6.9 8.5H3.8V21h3.1V8.5zM5.3 3.5A1.8 1.8 0 105.3 7a1.8 1.8 0 000-3.6M21 21v-6.9c0-3.3-1.8-4.9-4.2-4.9-1.9 0-2.8 1.1-3.3 1.8V8.5H10.5c0 .9.1 12.5.1 12.5h3.1v-7c0-.4 0-.7.1-.9.3-.6.8-1.3 1.8-1.3 1.3 0 1.9 1 1.9 2.5V21H21z' },
]
</script>

<template>
  <footer class="ftr" :class="{ 'ftr--pro': isPro }">
    <div class="container ftr__grid">
      <div class="ftr__col">
        <h3 class="ftr__title">{{ settings.agencies[0].name }}</h3>
        <address class="ftr__addr">
          {{ settings.agencies[0].street }}<br />
          {{ settings.agencies[0].zip }} {{ settings.agencies[0].town }}<br />
          <a :href="`tel:${settings.agencies[0].phone_tel}`">{{ settings.agencies[0].phone_display }}</a><br />
          <a :href="`mailto:${settings.email}`">{{ settings.email }}</a>
        </address>
        <p class="ftr__rating">
          <span class="star">★</span> Note Google {{ settings.rating.value }}/5
          ({{ settings.rating.count }} avis)
        </p>
      </div>

      <div class="ftr__col">
        <h3 class="ftr__title">Showroom {{ settings.agencies[1].city }}</h3>
        <address class="ftr__addr">
          {{ settings.agencies[1].street }}<br />
          {{ settings.agencies[1].zip }} {{ settings.agencies[1].town }}<br />
          <a :href="`tel:${settings.agencies[1].phone_tel}`">{{ settings.agencies[1].phone_display }}</a>
        </address>
      </div>

      <div class="ftr__col">
        <h3 class="ftr__title">Horaires</h3>
        <p class="ftr__addr">
          Mayenne &amp; La Baule<br />
          Lundi au Vendredi<br />
          8h00 à 12h00<br />
          13h30 à 18h00<br />
          <strong>Fermé le vendredi après-midi à Mayenne</strong>
        </p>
      </div>

      <div class="ftr__col ftr__col--logo">
        <router-link :to="isPro ? PATHS.pro : '/'" aria-label="Accueil">
          <img src="/opt/logo-400.webp" srcset="/opt/logo-200.webp 200w, /opt/logo-400.webp 400w" sizes="200px" width="200" height="84" alt="Les Ateliers du Terras" loading="lazy" />
        </router-link>
      </div>
    </div>

    <!-- Nos zones d'intervention (maillage interne) -->
    <div v-if="zoneSectors.length" class="ftr__zones">
      <div class="container">
        <h3 class="ftr__zones-title">Nos zones d'intervention</h3>
        <div class="ftr__zones-grid">
          <div v-for="grp in zoneSectors" :key="grp.sector" class="ftr__zones-col">
            <span class="ftr__zones-sector">{{ grp.sector }}</span>
            <ul class="ftr__zones-list">
              <li v-for="z in grp.zones" :key="z.path">
                <router-link :to="z.path">{{ z.title }}</router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="ftr__social">
      <a
        v-for="s in socials"
        :key="s.name"
        :href="s.url"
        class="ftr__pastille"
        target="_blank"
        rel="noopener"
        :aria-label="s.name"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
          <path :d="s.icon" />
        </svg>
      </a>
    </div>

    <div class="ftr__bottom">
      <div class="container ftr__bottom__in">
        <router-link :to="PATHS.mentions">Mentions légales</router-link>
        <router-link v-if="!isPro" :to="PATHS.pro">Espace Professionnels</router-link>
        <router-link v-else to="/">Site Particuliers</router-link>
        <span>© {{ year }} Les Ateliers du Terras - Dépannage Mayenne</span>
        <a href="https://juliengauthier.org" target="_blank" rel="noopener">
          Conception &amp; Tech par juliengauthier.org
        </a>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.ftr {
  background: var(--bg-alt2);
  border-top: 1px solid var(--border);
  margin-top: 40px;
}
.ftr__grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1.2fr;
  gap: 32px;
  padding-top: 48px;
  padding-bottom: 32px;
}
.ftr__title {
  font-size: 1rem;
  color: var(--title);
  text-transform: uppercase;
  letter-spacing: 0.02em;
  border-bottom: 2px solid var(--green);
  display: inline-block;
  padding-bottom: 6px;
  margin-bottom: 14px;
}
.ftr__addr {
  font-size: 0.92rem;
  line-height: 1.8;
  color: var(--ink);
  font-style: normal;
}
.ftr__addr a {
  color: var(--ink);
}
.ftr__addr a:hover {
  color: var(--green-dark);
}
.ftr__rating {
  margin-top: 12px;
  font-weight: 600;
  font-size: 0.9rem;
}
.star {
  color: var(--green);
}
.ftr__col--logo {
  display: flex;
  align-items: flex-start;
  justify-content: center;
}
.ftr__col--logo img {
  width: 200px;
  height: auto;
}
/* --- Zones d'intervention (maillage interne compact) --- */
.ftr__zones {
  border-top: 1px solid var(--border);
  padding: 26px 0 8px;
}
.ftr__zones-title {
  font-size: 0.95rem;
  color: var(--title);
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 16px;
}
.ftr__zones-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px 28px;
}
.ftr__zones-sector {
  display: block;
  color: var(--green-dark);
  font-weight: 700;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 8px;
}
.ftr__zones-list {
  list-style: none;
  margin: 0;
  padding: 0;
  columns: 2;
  column-gap: 18px;
}
.ftr__zones-list li {
  margin-bottom: 5px;
  break-inside: avoid;
}
.ftr__zones-list a {
  color: var(--ink);
  font-size: 0.85rem;
}
.ftr__zones-list a:hover {
  color: var(--green-dark);
}

.ftr__social {
  display: flex;
  justify-content: center;
  gap: 18px;
  padding: 8px 0 32px;
}
.ftr__pastille {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: var(--green);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, transform 0.15s;
}
.ftr__pastille:hover {
  background: var(--green-hover);
  transform: translateY(-2px);
  text-decoration: none;
}
.ftr__bottom {
  background: #fff;
  border-top: 1px solid var(--border);
  font-size: 0.82rem;
  color: var(--muted);
}
.ftr__bottom__in {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  flex-wrap: wrap;
  padding-top: 16px;
  padding-bottom: 16px;
  text-align: center;
}
.ftr__bottom a {
  color: var(--green-dark);
}

@media (max-width: 900px) {
  .ftr__grid {
    grid-template-columns: 1fr 1fr;
  }
  .ftr__col--logo {
    justify-content: flex-start;
  }
}
@media (max-width: 560px) {
  .ftr__grid {
    grid-template-columns: 1fr;
    gap: 26px;
  }
  .ftr__bottom__in {
    gap: 8px;
  }
  .ftr__zones-list {
    columns: 1;
  }
}

/* ===== Theme sombre PRO ===== */
.ftr--pro {
  background: #1c1e18;
  border-top-color: #3a3e33;
}
.ftr--pro .ftr__title,
.ftr--pro .ftr__zones-title {
  color: #f0f2ea;
}
.ftr--pro .ftr__addr,
.ftr--pro .ftr__addr a {
  color: #c4c9ba;
}
.ftr--pro .ftr__addr a:hover,
.ftr--pro .ftr__zones-list a:hover {
  color: var(--green);
}
.ftr--pro .ftr__zones {
  border-top-color: #3a3e33;
}
/* Sur le footer PRO sombre, le vert de texte du theme clair serait illisible :
   on repasse en lime, lisible sur charbon (>=7:1). */
.ftr--pro .ftr__zones-sector {
  color: var(--green);
}
.ftr--pro .ftr__zones-list a {
  color: #b7bdac;
}
.ftr--pro .ftr__bottom {
  background: #14160f;
  border-top-color: #3a3e33;
  color: #a7ac9c;
}
.ftr--pro .ftr__bottom a {
  color: var(--green);
}
</style>
